// pages/tests/MonthlyTestDetail.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Chip,
  Divider,
  Card,
  CardContent,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Assessment as AssessmentIcon,
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Publish as PublishIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  School as SchoolIcon,
  Subject as SubjectIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';

const MonthlyTestDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marks, setMarks] = useState([]);
  const [files, setFiles] = useState([]);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);

  // Sample data - replace with API calls
  const sampleTest = {
    id: 1,
    academicYear: '2024-2025',
    classId: 1,
    className: 'Class 10',
    sectionId: 1,
    sectionName: 'A',
    subjectId: 1,
    subjectName: 'Mathematics',
    testName: 'Monthly Test - January',
    testDate: '2024-01-15',
    maxMarks: 100,
    passingMarks: 35,
    instructions: 'Bring your own calculator. No mobile phones allowed.',
    status: 'PUBLISHED',
    createdBy: 'Admin',
    createdAt: '2024-01-01T10:00:00Z',
    totalStudents: 45,
    marksEntered: 45,
    averageMarks: 72.5,
    highestMarks: 98,
    lowestMarks: 28,
    passPercentage: 88.9
  };

  const sampleMarks = [
    { id: 1, studentId: 1, studentName: 'John Doe', rollNo: '001', marksObtained: 85, grade: 'A', absent: false, remarks: 'Excellent' },
    { id: 2, studentId: 2, studentName: 'Jane Smith', rollNo: '002', marksObtained: 92, grade: 'A+', absent: false, remarks: 'Outstanding' },
    { id: 3, studentId: 3, studentName: 'Bob Johnson', rollNo: '003', marksObtained: 0, grade: 'F', absent: true, remarks: 'Absent' },
    { id: 4, studentId: 4, studentName: 'Alice Brown', rollNo: '004', marksObtained: 78, grade: 'B+', absent: false, remarks: 'Good' },
    { id: 5, studentId: 5, studentName: 'Charlie Wilson', rollNo: '005', marksObtained: 65, grade: 'B', absent: false, remarks: 'Average' },
  ];

  const sampleFiles = [
    { id: 1, fileName: 'question_paper.pdf', fileType: 'application/pdf', fileSize: 1024000, uploadedAt: '2024-01-10T10:00:00Z', studentId: null },
    { id: 2, fileName: 'answer_key.pdf', fileType: 'application/pdf', fileSize: 512000, uploadedAt: '2024-01-10T10:30:00Z', studentId: null },
    { id: 3, fileName: 'john_doe_answer_sheet.pdf', fileType: 'application/pdf', fileSize: 2048000, uploadedAt: '2024-01-16T09:00:00Z', studentId: 1 },
  ];

  useEffect(() => {
    fetchTestDetails();
  }, [id]);

  const fetchTestDetails = async () => {
    setLoading(true);
    try {
      // const response = await axios.get(`/api/tests/${id}`);
      // setTest(response.data);
      
      setTimeout(() => {
        setTest(sampleTest);
        setMarks(sampleMarks);
        setFiles(sampleFiles);
        setLoading(false);
      }, 500);
    } catch (error) {
      console.error('Error fetching test details:', error);
      setLoading(false);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      DRAFT: { color: 'default', icon: <ScheduleIcon />, label: 'Draft' },
      OPEN: { color: 'warning', icon: <AssessmentIcon />, label: 'Open' },
      COMPLETED: { color: 'info', icon: <CheckCircleIcon />, label: 'Completed' },
      PUBLISHED: { color: 'success', icon: <PublishIcon />, label: 'Published' }
    };

    const config = statusConfig[status] || statusConfig.DRAFT;
    
    return (
      <Chip
        label={config.label}
        color={config.color}
        icon={config.icon}
      />
    );
  };

  const getGradeColor = (grade) => {
    const colors = {
      'A+': 'success',
      'A': 'success',
      'B+': 'info',
      'B': 'info',
      'C': 'warning',
      'D': 'warning',
      'F': 'error'
    };
    return colors[grade] || 'default';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const handlePublish = async () => {
    try {
      // await axios.patch(`/api/tests/${id}/publish`);
      setTest({ ...test, status: 'PUBLISHED' });
      setPublishDialogOpen(false);
    } catch (error) {
      console.error('Error publishing test:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (!test) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Test not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/tests')}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1">
              {test.testName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              {getStatusChip(test.status)}
              <Chip label={test.academicYear} size="small" variant="outlined" />
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
          >
            Print
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/tests/${id}/edit`)}
          >
            Edit
          </Button>
          {test.status !== 'PUBLISHED' && (
            <Button
              variant="contained"
              color="success"
              startIcon={<PublishIcon />}
              onClick={() => setPublishDialogOpen(true)}
            >
              Publish
            </Button>
          )}
        </Box>
      </Box>

      {/* Info Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <SchoolIcon color="primary" fontSize="small" />
                <Typography color="textSecondary" variant="body2">
                  Class & Section
                </Typography>
              </Box>
              <Typography variant="h6">
                {test.className} - {test.sectionName}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <SubjectIcon color="primary" fontSize="small" />
                <Typography color="textSecondary" variant="body2">
                  Subject
                </Typography>
              </Box>
              <Typography variant="h6">{test.subjectName}</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CalendarIcon color="primary" fontSize="small" />
                <Typography color="textSecondary" variant="body2">
                  Test Date
                </Typography>
              </Box>
              <Typography variant="h6">
                {format(new Date(test.testDate), 'dd MMM yyyy')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AssessmentIcon color="primary" fontSize="small" />
                <Typography color="textSecondary" variant="body2">
                  Marks
                </Typography>
              </Box>
              <Typography variant="h6">
                {test.passingMarks}/{test.maxMarks}
                <Typography component="span" variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                  (Pass/Total)
                </Typography>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Statistics */}
      {test.status === 'PUBLISHED' && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Test Statistics
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={6} sm={3}>
              <Typography color="textSecondary" variant="body2">Average Marks</Typography>
              <Typography variant="h5">{test.averageMarks}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography color="textSecondary" variant="body2">Highest Marks</Typography>
              <Typography variant="h5" color="success.main">{test.highestMarks}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography color="textSecondary" variant="body2">Lowest Marks</Typography>
              <Typography variant="h5" color="error.main">{test.lowestMarks}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography color="textSecondary" variant="body2">Pass Percentage</Typography>
              <Typography variant="h5">{test.passPercentage}%</Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Instructions */}
      {test.instructions && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Instructions
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {test.instructions}
          </Typography>
        </Paper>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={`Marks (${marks.length})`} icon={<AssessmentIcon />} iconPosition="start" />
          <Tab label={`Files (${files.length})`} icon={<PdfIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Roll No</TableCell>
                <TableCell>Student Name</TableCell>
                <TableCell align="center">Marks Obtained</TableCell>
                <TableCell align="center">Grade</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell>Remarks</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {marks.map((mark) => (
                <TableRow key={mark.id} hover>
                  <TableCell>{mark.rollNo}</TableCell>
                  <TableCell>{mark.studentName}</TableCell>
                  <TableCell align="center">
                    {mark.absent ? '-' : `${mark.marksObtained}/${test.maxMarks}`}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={mark.grade}
                      color={getGradeColor(mark.grade)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {mark.absent ? (
                      <Chip label="Absent" color="error" size="small" />
                    ) : mark.marksObtained >= test.passingMarks ? (
                      <Chip label="Pass" color="success" size="small" />
                    ) : (
                      <Chip label="Fail" color="error" size="small" />
                    )}
                  </TableCell>
                  <TableCell>{mark.remarks}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {activeTab === 1 && (
        <Paper>
          <List>
            {files.map((file) => (
              <ListItem key={file.id} divider>
                <ListItemIcon>
                  <PdfIcon color="error" />
                </ListItemIcon>
                <ListItemText
                  primary={file.fileName}
                  secondary={
                    <>
                      {formatFileSize(file.fileSize)} • 
                      Uploaded {format(new Date(file.uploadedAt), 'dd MMM yyyy')}
                      {file.studentId && ' • Student specific'}
                    </>
                  }
                />
                <Tooltip title="Download">
                  <IconButton edge="end">
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
              </ListItem>
            ))}
            {files.length === 0 && (
              <ListItem>
                <ListItemText primary="No files uploaded" />
              </ListItem>
            )}
          </List>
        </Paper>
      )}

      {/* Publish Dialog */}
      <Dialog open={publishDialogOpen} onClose={() => setPublishDialogOpen(false)}>
        <DialogTitle>Publish Test Results</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to publish the results for "{test.testName}"? 
            Once published, students and parents will be able to view the results.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishDialogOpen(false)}>Cancel</Button>
          <Button onClick={handlePublish} variant="contained" color="success">
            Publish Results
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MonthlyTestDetails;